import type { BadgeTone } from "@/components/ui/Badge";

import type { InventoryDiscrepancySkuContextRow } from "./inventoryDiscrepancyApi";

export type InventoryDiscrepancySkuContextRowWithRunningTotal = InventoryDiscrepancySkuContextRow & {
  /** Physical on-hand after this row. Null when the row is excluded from the ledger. */
  on_hand_total: number | null;
  /**
   * In-house balance after this row. Includes inbound receipts before a purchase invoice
   * and allocated quantity that has not left the building. Null when the row is excluded.
   */
  in_house_total: number | null;
};

/** Matches ui `EM_DASH` — placeholder when a balance is null (cancelled or pending rows). */
export const SKU_CONTEXT_EMPTY_RUNNING_TOTAL = "\u2014";

const SKU_CONTEXT_KIND_LABELS: Readonly<Record<string, string>> = {
  adjustment: "Adjustment",
  wastage: "Wastage",
  prototype: "Prototype",
  transfer_pack: "Transfer pack",
  inbound_receipt: "Inbound receipt",
  purchase_invoice: "Purchase invoice",
  purchase_order: "Purchase order",
  sales_order: "Sales order",
  transfer_in: "Transfer in",
  transfer_out: "Transfer out",
  work_order: "Work order",
  work_order_allocation: "Work order allocation",
  work_order_consumption: "Finished good issue",
};

const SKU_CONTEXT_DETAIL_STATUS_LABELS: Readonly<Record<string, string>> = {
  cancelled: "Cancelled",
  canceled: "Cancelled",
  pending: "Pending",
  received: "Received",
  closed: "Closed",
  verified: "Verified",
};

/** Title-case one word for unknown status tokens. */
function titleCaseWord(word: string): string {
  if (word.length === 0) {
    return word;
  }
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

/** Sentence-case unknown kind tokens (first word capitalized, rest lowercase). */
function sentenceCaseTokens(tokens: string[]): string {
  return tokens
    .map((word, index) => (index === 0 ? titleCaseWord(word) : word.toLowerCase()))
    .join(" ");
}

/** Normalize transaction kind for the Kind column and CSV export. */
export function formatSkuContextKind(kind: string): string {
  const trimmed = kind.trim();
  if (trimmed === "") {
    return SKU_CONTEXT_EMPTY_RUNNING_TOTAL;
  }
  const known = SKU_CONTEXT_KIND_LABELS[trimmed.toLowerCase()];
  if (known != null) {
    return known;
  }
  const tokens = trimmed.split(/[\s_]+/).filter((part) => part.length > 0);
  if (tokens.length === 0) {
    return SKU_CONTEXT_EMPTY_RUNNING_TOTAL;
  }
  return sentenceCaseTokens(tokens);
}

const TRANSFER_ORDER_STATUSES = new Set([
  "pending",
  "packing",
  "ready_to_ship",
  "shipped",
  "delivered",
  "partially_received",
  "received",
  "cancelled",
]);

function transferOrderStatusBadgeTone(status: string): BadgeTone {
  switch (status) {
    case "pending":
      return "warning";
    case "packing":
    case "ready_to_ship":
    case "shipped":
      return "info";
    case "delivered":
    case "received":
      return "success";
    case "partially_received":
      return "warning";
    case "cancelled":
      return "muted";
    default:
      return "neutral";
  }
}

function isHubTransferContextKind(kind: string | null | undefined): boolean {
  const normalized = (kind ?? "").trim().toLowerCase();
  return normalized === "transfer_in" || normalized === "transfer_out";
}

/** Badge tone for transaction status; transfer orders mirror transfers workbench tones. */
export function skuContextDetailStatusBadgeTone(
  detail: string | null,
  kind?: string | null,
): BadgeTone | null {
  if (detail == null) {
    return null;
  }
  const trimmed = detail.trim();
  if (trimmed === "") {
    return null;
  }
  const normalized = trimmed.toLowerCase().replace(/\s+/g, "_");
  if (isHubTransferContextKind(kind) && TRANSFER_ORDER_STATUSES.has(normalized)) {
    return transferOrderStatusBadgeTone(normalized);
  }
  if (
    normalized === "verified"
    || normalized === "received"
    || normalized === "closed"
    || normalized === "completed"
    || normalized === "delivered"
    || normalized === "posted_to_mv"
  ) {
    return "success";
  }
  if (normalized === "pending" || normalized === "open" || normalized === "submitted") {
    return "info";
  }
  if (normalized === "cancelled" || normalized === "canceled") {
    return "danger";
  }
  if (normalized === "fullyinvoiced" || normalized === "fully_invoiced") {
    return "muted";
  }
  if (normalized.includes("partial") || normalized === "draft") {
    return "warning";
  }
  return "neutral";
}

/** Normalize hub/MV status strings for the Status column and CSV export. */
export function formatSkuContextDetailStatus(detail: string | null): string {
  if (detail == null) {
    return SKU_CONTEXT_EMPTY_RUNNING_TOTAL;
  }
  const trimmed = detail.trim();
  if (trimmed === "") {
    return SKU_CONTEXT_EMPTY_RUNNING_TOTAL;
  }
  const known = SKU_CONTEXT_DETAIL_STATUS_LABELS[trimmed.toLowerCase()];
  if (known != null) {
    return known;
  }
  return trimmed
    .split(/[\s_]+/)
    .filter((part) => part.length > 0)
    .map(titleCaseWord)
    .join(" ");
}

/** Hub transfer statuses before the goods leave. Packed quantity stays in parentheses. */
const TRANSFER_NOT_YET_SHIPPED_DETAILS = new Set(["pending", "packing", "ready_to_ship"]);

/** Hub transfer statuses from ship onward. Packed quantity is the posted movement. */
const TRANSFER_SHIPPED_OR_LATER_DETAILS = new Set([
  "shipped",
  "delivered",
  "partially_received",
  "received",
]);

function normalizedTransferDetail(detail: string | null): string {
  return (detail ?? "").trim().toLowerCase().replace(/\s+/g, "_");
}

/** Hub transfer rows carry a packed quantity. Legacy Megaventory transfers leave it null. */
function isHubTransferPackedRow(
  kind: string,
  packedQuantity: number | null | undefined,
): boolean {
  if (!isHubTransferContextKind(kind) || packedQuantity == null || !Number.isFinite(packedQuantity)) {
    return false;
  }
  return true;
}

function hubTransferHasShipped(detail: string | null): boolean {
  return TRANSFER_SHIPPED_OR_LATER_DETAILS.has(normalizedTransferDetail(detail));
}

function signedHubTransferPackedQuantity(kind: string, packedQuantity: number): number {
  const packed = Math.max(0, Math.trunc(packedQuantity));
  // Avoid -0: node:assert/strict uses Object.is, so -0 !== 0.
  if (packed === 0) {
    return 0;
  }
  return kind.trim().toLowerCase() === "transfer_out" ? -packed : packed;
}

function isHubTransferPackingAllocationRow(kind: string, detail: string | null): boolean {
  if (!isHubTransferContextKind(kind)) {
    return false;
  }
  return TRANSFER_NOT_YET_SHIPPED_DETAILS.has(normalizedTransferDetail(detail));
}

function isAllocationOnlySkuContextRow(kind: string, detail: string | null): boolean {
  const normalizedKind = kind.trim().toLowerCase();
  if (normalizedKind === "work_order_allocation") {
    return true;
  }
  return isHubTransferPackingAllocationRow(kind, detail);
}

/**
 * Signed physical on-hand change.
 * Inbound receipts and in-building allocations do not move on-hand.
 * A purchase invoice is the receipt quantity becoming on-hand.
 * Returns null when the row is cancelled or pending.
 */
export function sgsSkuContextRowOnHandEffect(
  kind: string,
  quantity: number | null,
  detail: string | null = null,
  packedQuantity: number | null = null,
): number | null {
  if (isCancelledSkuContextDetail(detail)) {
    return null;
  }
  if (isHubTransferPackedRow(kind, packedQuantity)) {
    if (!hubTransferHasShipped(detail)) {
      return 0;
    }
    return signedHubTransferPackedQuantity(kind, packedQuantity ?? 0);
  }
  if (isExcludedFromSkuContextRunningTotal(detail)) {
    return null;
  }
  if (kind.trim().toLowerCase() === "inbound_receipt") {
    return 0;
  }
  if (isAllocationOnlySkuContextRow(kind, detail)) {
    return 0;
  }
  return sgsSkuContextRowInventoryEffect(kind, quantity, detail);
}

/**
 * Signed in-house change. Inbound receipts count here before a purchase invoice.
 * Packing and ready-to-ship hub transfers reduce in-house by sealed (packed) quantity only.
 * Work-order allocations stay in house, so they do not move this balance.
 * Returns null when the row is cancelled or pending.
 */
export function sgsSkuContextRowInHouseEffect(
  kind: string,
  quantity: number | null,
  detail: string | null = null,
  packedQuantity: number | null = null,
): number | null {
  if (isCancelledSkuContextDetail(detail)) {
    return null;
  }
  if (isHubTransferPackedRow(kind, packedQuantity)) {
    return signedHubTransferPackedQuantity(kind, packedQuantity ?? 0);
  }
  if (isExcludedFromSkuContextRunningTotal(detail)) {
    return null;
  }
  if (isAllocationOnlySkuContextRow(kind, detail)) {
    return 0;
  }
  return sgsSkuContextRowInventoryEffect(kind, quantity, detail);
}

/** Table/CSV display: on hand, then in house in parentheses. Null pair renders an em dash. */
export function formatSkuContextRunningTotalDisplay(
  onHandTotal: number | null | undefined,
  inHouseTotal: number | null | undefined,
): string {
  if (onHandTotal == null || inHouseTotal == null) {
    return SKU_CONTEXT_EMPTY_RUNNING_TOTAL;
  }
  return `${String(onHandTotal)} (${String(inHouseTotal)})`;
}

/** Detail values that indicate the transaction did not affect SGS on-hand. */
export function isCancelledSkuContextDetail(detail: string | null): boolean {
  if (detail == null) {
    return false;
  }
  const normalized = detail.trim().toLowerCase();
  return normalized === "cancelled" || normalized === "canceled";
}

/** Detail values that should appear in the ledger but not move the running total yet. */
export function isPendingSkuContextDetail(detail: string | null): boolean {
  if (detail == null) {
    return false;
  }
  return detail.trim().toLowerCase() === "pending";
}

/** Cancelled and pending rows are visible but excluded from running totals. */
export function isExcludedFromSkuContextRunningTotal(detail: string | null): boolean {
  return isCancelledSkuContextDetail(detail) || isPendingSkuContextDetail(detail);
}

/**
 * Signed SGS on-hand change for one context row (null quantity counts as 0).
 * Returns null when the row is cancelled or pending and should not affect running totals.
 */
export function sgsSkuContextRowInventoryEffect(
  kind: string,
  quantity: number | null,
  detail: string | null = null,
): number | null {
  if (isExcludedFromSkuContextRunningTotal(detail)) {
    return null;
  }
  const qty = quantity ?? 0;
  if (kind === "transfer_out" || kind === "sales_order" || kind === "work_order_allocation") {
    return -qty;
  }
  return qty;
}

/** Signed quantity for display/export: +N, -N, or em dash when quantity is null. */
export function formatSignedSkuContextQuantity(
  row: Pick<InventoryDiscrepancySkuContextRow, "kind" | "quantity" | "detail" | "packed_quantity">,
): string {
  if (row.kind.trim().toLowerCase() === "inbound_receipt") {
    if (row.quantity == null) {
      return SKU_CONTEXT_EMPTY_RUNNING_TOTAL;
    }
    const signed = row.quantity > 0 ? `+${String(row.quantity)}` : String(row.quantity);
    return `(${signed})`;
  }
  if (isHubTransferPackedRow(row.kind, row.packed_quantity)) {
    const signedPacked = signedHubTransferPackedQuantity(row.kind, row.packed_quantity ?? 0);
    const packedLabel = signedPacked > 0 ? `+${String(signedPacked)}` : String(signedPacked);
    if (!hubTransferHasShipped(row.detail)) {
      return `(${packedLabel})`;
    }
    return packedLabel;
  }
  if (row.quantity == null) {
    return SKU_CONTEXT_EMPTY_RUNNING_TOTAL;
  }
  const signed =
    row.kind === "transfer_out" || row.kind === "sales_order" || row.kind === "work_order_allocation"
      ? -row.quantity
      : row.quantity;
  if (signed > 0) {
    return `+${String(signed)}`;
  }
  return String(signed);
}

/**
 * Attach on-hand and in-house balances. Rows arrive newest-first.
 *
 * When `sgsOnHand` is set, that physical inventory is the newest balance. Older rows are
 * that balance with each newer transaction removed. In-house starts at the same physical
 * inventory plus still-open extras in the window (unmatched receipts, packed transfers
 * that have not posted). History rows are not summed to invent the current balance.
 * Cancelled and pending rows are excluded and show null balances.
 */
export function attachRunningTotalsToSkuContextRows(
  rows: InventoryDiscrepancySkuContextRow[],
  sgsOnHand: number | null = null,
): InventoryDiscrepancySkuContextRowWithRunningTotal[] {
  const indexed = rows.map((row, displayIndex) => ({ row, displayIndex }));

  const chronological = [...indexed].sort((left, right) => {
    const parseTime = (value: string | null): number => {
      if (value == null || value.trim() === "") {
        return Number.NEGATIVE_INFINITY;
      }
      const parsed = Date.parse(value);
      return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
    };

    const timeDelta = parseTime(left.row.occurred_at) - parseTime(right.row.occurred_at);
    if (timeDelta !== 0) {
      return timeDelta;
    }
    // Same timestamp: higher display index is older in the API's desc-sorted window.
    return right.displayIndex - left.displayIndex;
  });

  const effects = chronological.map(({ row, displayIndex }) => ({
    displayIndex,
    onHandEffect: sgsSkuContextRowOnHandEffect(
      row.kind,
      row.quantity,
      row.detail,
      row.packed_quantity ?? null,
    ),
    inHouseEffect: sgsSkuContextRowInHouseEffect(
      row.kind,
      row.quantity,
      row.detail,
      row.packed_quantity ?? null,
    ),
  }));

  const onHandByDisplayIndex = new Map<number, number | null>();
  const inHouseByDisplayIndex = new Map<number, number | null>();
  const anchored = sgsOnHand != null && Number.isFinite(sgsOnHand);

  if (anchored) {
    let openInHouseExtra = 0;
    for (const effect of effects) {
      if (effect.onHandEffect == null || effect.inHouseEffect == null) {
        continue;
      }
      openInHouseExtra += effect.inHouseEffect - effect.onHandEffect;
    }
    let onHand = sgsOnHand;
    let inHouse = sgsOnHand + openInHouseExtra;
    for (let index = effects.length - 1; index >= 0; index -= 1) {
      const effect = effects[index];
      if (effect == null || effect.onHandEffect == null || effect.inHouseEffect == null) {
        if (effect != null) {
          onHandByDisplayIndex.set(effect.displayIndex, null);
          inHouseByDisplayIndex.set(effect.displayIndex, null);
        }
        continue;
      }
      onHandByDisplayIndex.set(effect.displayIndex, onHand);
      inHouseByDisplayIndex.set(effect.displayIndex, inHouse);
      onHand -= effect.onHandEffect;
      inHouse -= effect.inHouseEffect;
    }
  } else {
    let onHand = 0;
    let inHouse = 0;
    for (const effect of effects) {
      if (effect.onHandEffect === null || effect.inHouseEffect === null) {
        onHandByDisplayIndex.set(effect.displayIndex, null);
        inHouseByDisplayIndex.set(effect.displayIndex, null);
        continue;
      }
      onHand += effect.onHandEffect;
      inHouse += effect.inHouseEffect;
      onHandByDisplayIndex.set(effect.displayIndex, onHand);
      inHouseByDisplayIndex.set(effect.displayIndex, inHouse);
    }
  }

  return rows.map((row, displayIndex) => ({
    ...row,
    on_hand_total: onHandByDisplayIndex.get(displayIndex) ?? null,
    in_house_total: inHouseByDisplayIndex.get(displayIndex) ?? null,
  }));
}
